import { Injectable } from '@angular/core';
import { ToastController, Platform, LoadingController, AlertController } from '@ionic/angular';
import * as moment from 'moment';

const SameDigitsCNPJ = [
    '00000000000000',
    '11111111111111',
    '22222222222222',
    '33333333333333',
    '44444444444444',
    '55555555555555',
    '66666666666666',
    '77777777777777',
    '88888888888888',
    '99999999999999'
];

@Injectable({
    providedIn: 'root'
})
export class IonicUtilService {

    private loading: any;
    public cordova = false;
    public widthPlatform: any;
    public heightPlatform: any;
    private maxWidth = 640;

    constructor(private platform: Platform,
        private toastCtrl: ToastController,
        private loadingCtrl: LoadingController,
        private alertCtrl: AlertController) {
        this.cordova = this.platform.is('cordova');
        platform.ready().then(() => {
            this.widthPlatform = platform.width() <= this.maxWidth ? platform.width() : this.maxWidth;
            this.heightPlatform = platform.height();
        });
    }

    async onLoading(message: string = '') {
        this.loading = await this.loadingCtrl.create({
            message,
            cssClass: 'sipni-loader'
        });
        return this.loading.present();
    }

    async endLoading() {
        let topLoader = await this.loadingCtrl.getTop();
        while (topLoader) {
            if (!(await topLoader.dismiss())) {
                return false;
            }
            topLoader = await this.loadingCtrl.getTop();
        }
    }

    async alert(title, message, buttons) {
        const alert = await this.alertCtrl.create({
            message,
            buttons
        });
        await alert.present();
    }

    async toast(message: string, duration = 6000) {
        if (message.length > 600) {
            duration = 10000;
        }

        const toast = await this.toastCtrl.create({
            message,
            position: 'bottom',
            duration,
            color: 'dark'
        });
        toast.present();
    }

    async toastError(message: string, duration = 6000) {
        const toast = await this.toastCtrl.create({
            message,
            position: 'bottom',
            duration,
            cssClass: 'toast-error',
            color: 'danger'
        });
        toast.present();
    }

    formatarCEP(cep) {
        const re = /^([\d]{2})\.*([\d]{3})-*([\d]{3})/;
        return cep.replace(re, '$1.$2-$3');
    }

    formatarCpfCnpj(valor) {
        if (!valor) {
            return '';
        }
        if (valor.length === 11) {
            return valor.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '\$1.\$2.\$3\-\$4');
        }
        return valor.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, '\$1.\$2.\$3\/\$4\-\$5');
    }

    removerFormatacao(texto: any) {
        return texto ? texto.replace(/[^\w\s]/gi, '') : null;
    }

    validarEmail(email) {
        if (email) {
            const regex = /^[a-z0-9.]+@[a-z0-9]+\.[a-z]+(\.[a-z]+)?$/i;
            return regex.test(email);
        } else {
            return false;
        }
    }

    async validarCpf(cpf) {
        cpf = cpf.replace(/\D/g, '');
        if (cpf.toString().length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
            return false;
        }
        let result = true;
        [9, 10].forEach((j) => {
            let soma = 0, r;
            cpf.split(/(?=)/).splice(0, j).forEach((e, i) => {
                soma += Number(e) * ((j + 2) - (i + 1));
            });
            r = soma % 11;
            r = (r < 2) ? 0 : 11 - r;
            if (r !== Number(cpf.substring(j, j + 1))) {
                result = false;
            }
        });
        return result;
    }

    validarCNPJ(cnpj) {
        if (!cnpj || !cnpj.trim()) {
            return false;
        }

        cnpj = cnpj.replace(/\D/gi, '');

        if (cnpj.length < 14) {
            return false;
        }

        // Elimina CNPJs invalidos conhecidos
        if (SameDigitsCNPJ.indexOf(cnpj) >= 0) {
            return false;
        }

        // Valida DVs
        let tamanho = cnpj.length - 2;
        let soma = 0;
        let pos = tamanho - 7;
        let numeros = cnpj.substring(0, tamanho);
        const digitos = cnpj.substring(tamanho);

        for (let i = tamanho; i >= 1; i--) {
            soma += numeros.charAt(tamanho - i) * pos--;
            if (pos < 2) {
                pos = 9;
            }
        }

        let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
        if (resultado.toString() !== digitos.charAt(0)) {
            return false;
        }

        tamanho = tamanho + 1;
        numeros = cnpj.substring(0, tamanho);
        soma = 0;
        pos = tamanho - 7;

        for (let i = tamanho; i >= 1; i--) {
            soma += numeros.charAt(tamanho - i) * pos--;
            if (pos < 2) {
                pos = 9;
            }
        }

        resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;

        if (resultado.toString() !== digitos.charAt(1)) {
            return false;
        }

        return true;
    }

    orderById(a, b) {
        if (a.id > b.id) {
            return 1;
        }
        if (a.id < b.id) {
            return -1;
        }
        return 0;
    }

    orderByCodigo(a, b) {
        if (a.codigo > b.codigo) {
            return 1;
        }
        if (a.codigo < b.codigo) {
            return -1;
        }
        return 0;
    }

    orderByDataAplicacao(a, b) {
        if (moment(a.dataAplicacao, 'YYYY-MM-DD').valueOf() > moment(b.dataAplicacao, 'YYYY-MM-DD').valueOf()) {
            return -1;
        }
        if (moment(a.dataAplicacao, 'YYYY-MM-DD').valueOf() < moment(b.dataAplicacao, 'YYYY-MM-DD').valueOf()) {
            return 1;
        }
        return 0;
    }

    orderByDataAprazamento(a, b) {
        if (moment(a.dataAprazamento, 'YYYY-MM-DD').valueOf() > moment(b.dataAprazamento, 'YYYY-MM-DD').valueOf()) {
            return -1;
        }
        if (moment(a.dataAprazamento, 'YYYY-MM-DD').valueOf() < moment(b.dataAprazamento, 'YYYY-MM-DD').valueOf()) {
            return 1;
        }
        return 0;
    }

    orderBySigla(a, b) {
        if (a.sigla.toLowerCase() > b.sigla.toLowerCase()) {
            return 1;
        }
        if (a.sigla.toLowerCase() < b.sigla.toLowerCase()) {
            return -1;
        }
        return 0;
    }

    orderByNome(a, b) {
        if (a.nome.toLowerCase() > b.nome.toLowerCase()) {
            return 1;
        }
        if (a.nome.toLowerCase() < b.nome.toLowerCase()) {
            return -1;
        }
        return 0;
    }

    orderByDescricao(a, b) {
        if (a.descricao.toLowerCase() > b.descricao.toLowerCase()) {
            return 1;
        }
        if (a.descricao.toLowerCase() < b.descricao.toLowerCase()) {
            return -1;
        }
        return 0;
    }

    orderByNumeroLote(a, b) {
        if (a.lote > b.lote) {
            return 1;
        }
        if (a.lote < b.lote) {
            return -1;
        }
        return 0;
    }

    orderByRazaoSocial(a, b) {
        if (a.razaoSocial > b.razaoSocial) {
            return 1;
        }
        if (a.razaoSocial < b.razaoSocial) {
            return -1;
        }
        return 0;
    }

    orderByNomeFabricante(a, b) {
        if (a.nomeFabricante > b.nomeFabricante) {
            return 1;
        }
        if (a.nomeFabricante < b.nomeFabricante) {
            return -1;
        }
        return 0;
    }

    orderByAbreviatura(a, b) {
        if (a.abreviatura > b.abreviatura) {
            return 1;
        }
        if (a.abreviatura < b.abreviatura) {
            return -1;
        }
        return 0;
    }

    orderByValue = (a, b) => {
        if (a.value > b.value) {
            return -1;
        }
        if (a.value < b.value) {
            return 1;
        }
        return 0;
    }
    orderByName = (a, b) => {
        if (a.name > b.name) {
            return 1;
        }
        if (a.name < b.name) {
            return -1;
        }
        return 0;
    }

    orderByNuApresentacao = (a, b) => {
        if (a.numeroApresentacao > b.numeroApresentacao) {
            return 1;
        }
        if (a.numeroApresentacao < b.numeroApresentacao) {
            return -1;
        }
        return 0;
    }

    isFloat(value) {
        if (typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value)) {
            return true;
        }
        return false;
    }
}
